import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

// Normalise a user payload from the API so `roles`, `active_role`, and the legacy
// `role` field are all in sync regardless of which endpoint produced it.
function normaliseUser(data, activeRole) {
  if (!data) return null;
  const roles = Array.isArray(data.roles) && data.roles.length
    ? data.roles
    : (data.role ? [data.role] : []);
  const active = activeRole || data.active_role || data.role || roles[0] || null;
  return {
    ...data,
    roles,
    active_role: active,
    role: active, // legacy: keep equal to active for any callers still reading user.role
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("qd_token");
    const cached = localStorage.getItem("qd_user");
    if (token && cached) {
      const parsed = normaliseUser(JSON.parse(cached));
      setUser(parsed);
      api.get("/auth/me").then((r) => {
        const fresh = normaliseUser(r.data);
        setUser(fresh);
        localStorage.setItem("qd_user", JSON.stringify(fresh));
      }).catch(() => {
        localStorage.removeItem("qd_token");
        localStorage.removeItem("qd_user");
        setUser(null);
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = (token, userData, activeRole) => {
    const normalised = normaliseUser(userData, activeRole);
    localStorage.setItem("qd_token", token);
    localStorage.setItem("qd_user", JSON.stringify(normalised));
    setUser(normalised);
  };

  const logout = () => {
    localStorage.removeItem("qd_token");
    localStorage.removeItem("qd_user");
    setUser(null);
  };

  // Switch active session role to one the user already has (e.g. shopkeeper ↔ customer).
  // Backend re-issues the JWT with the new active_role.
  const switchRole = async (newRole) => {
    const r = await api.post("/auth/switch-role", { role: newRole });
    const { token, user: u, active_role } = r.data;
    const normalised = normaliseUser(u, active_role);
    localStorage.setItem("qd_token", token);
    localStorage.setItem("qd_user", JSON.stringify(normalised));
    setUser(normalised);
    return normalised;
  };

  const hasRole = (role) => Array.isArray(user?.roles) && user.roles.includes(role);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, switchRole, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
